import { Link } from "@mui/material";
import { Button, ConfirmationModal, Typography } from "@platform/design-system";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { useApi } from "@platform/api";

import useToastNotificationService from "../hooks/useToastNotificationService";
import { downloadBinaryFile } from "../utils";

import { SuccessMessageWithCheckCircleIcon } from "./successMessageWithCheckCircleIcon";

interface Props {
  rfpId: string;
  onDelete: () => Promise<void>;
  isDeleting: boolean;
}

function ProcessedCensus({
  rfpId,
  onDelete: onRemove,
  isDeleting: isRemoving,
}: Props): React.JSX.Element {
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const { createToastNotification } = useToastNotificationService();
  const axiosInstance = useApi();
  const { mutate: downloadCensus, isPending: isDownloading } = useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.get<Blob>(
        `brokerportal/v1/rfp/${rfpId}/census-document/download`,
        {
          responseType: "blob",
        },
      );
      return data;
    },
    onSuccess: (data) => {
      downloadBinaryFile(data, `Census_file_for_rfp_${rfpId}.xlsx`);
    },
    onError: () => {
      createToastNotification({
        severity: "error",
        message: "An error occurred while downloading the census document.",
      });
    },
  });

  const handleRemoveConfirmed = async (): Promise<void> => {
    await onRemove();
    setShowRemoveModal(false);
  };

  // Create a handler to avoid passing downloadCensus directly
  // to prevent the mutation from getting event as parameter
  const handleDownload = (): void => {
    downloadCensus();
  };

  return (
    <SuccessMessageWithCheckCircleIcon
      title="Thank you"
      description="This census has been successfully processed."
    >
      <Typography>
        Click{" "}
        <Link
          sx={{
            fontWeight: 600,
            textDecoration: "none",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          color="brandPrimary.dark"
          component="button"
          onClick={handleDownload}
          disabled={isDownloading}
        >
          {isDownloading ? "Downloading..." : "here"}
        </Link>{" "}
        to download the newly structured census.
      </Typography>
      <Button
        variant="secondary"
        sx={{ mt: 5 }}
        onClick={() => setShowRemoveModal(true)}
      >
        REMOVE PROCESSED CENSUS
      </Button>
      <ConfirmationModal
        open={showRemoveModal}
        onCancel={() => setShowRemoveModal(false)}
        onAccept={handleRemoveConfirmed}
        title="Remove processed census?"
        description="Proceed with removing processed census data?"
        confirmButtonText="PROCEED"
        cancelButtonText="CANCEL"
        loading={isRemoving}
      />
    </SuccessMessageWithCheckCircleIcon>
  );
}

export { ProcessedCensus };
